/**
 * RotateDeviceNotice — full-viewport "rotate your device" treatment for the
 * phone-landscape policy (see viewportOrientationPolicy.ts).
 *
 * Contract:
 *  - Leaf component: depends only on React + the pure policy helper. No game
 *    logic, no ServiceRegistry, no circular imports.
 *  - Mounted at the app root (src/main.tsx) so it covers EVERY surface
 *    (splash, gallery, creators, popout, in-game HUD).
 *  - Visible immediately on mount when the policy is true, and re-evaluated on
 *    window resize / orientationchange; it renders null (unmounts) the moment
 *    the policy turns false. The game surface underneath stays mounted — this
 *    component never navigates or tears anything down.
 *  - No automatic fullscreen. `screen.orientation.lock('landscape')` is only
 *    attempted best-effort from an explicit user gesture ("Rotate now"), inside
 *    try/catch, and a rejected promise is swallowed. Environments without the
 *    API simply do not render the control.
 *  - Playwright contract: `[data-testid="rotate-device-notice"]`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { shouldShowRotateNotice } from './viewportOrientationPolicy';

interface ViewportSize {
  width: number;
  height: number;
}

/** Read the live viewport; SSR-safe neutral fallback. */
function readViewportSize(): ViewportSize {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  return { width: window.innerWidth, height: window.innerHeight };
}

/** Viewport size state that tracks resize + orientationchange events. */
function useViewportSize(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(readViewportSize);

  useEffect(() => {
    const handleViewportChange = (): void => {
      const next = readViewportSize();
      setSize(previous =>
        previous.width === next.width && previous.height === next.height ? previous : next,
      );
    };
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    // Catch a resize that happened between first render and effect subscription.
    handleViewportChange();
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, []);

  return size;
}

/**
 * Minimal structural view of the Screen Orientation API. This repo's DOM lib
 * pin does not declare `ScreenOrientation.lock()`, and the API is absent on
 * some mobile browsers at runtime, so we feature-detect structurally instead
 * of casting to the DOM type.
 */
interface LandscapeOrientationLock {
  lock: (orientation: 'landscape') => Promise<unknown> | void;
}

/** Returns a bound best-effort landscape lock, or null when unsupported. */
function readLandscapeLock(): LandscapeOrientationLock['lock'] | null {
  if (typeof window === 'undefined' || !window.screen) return null;
  const orientation = (window.screen as unknown as { orientation?: Partial<LandscapeOrientationLock> })
    .orientation;
  if (!orientation || typeof orientation.lock !== 'function') return null;
  return orientation.lock.bind(orientation);
}

/** Feature check: is a best-effort landscape lock even possible here? */
function canAttemptLandscapeLock(): boolean {
  return readLandscapeLock() !== null;
}

/**
 * Best-effort `screen.orientation.lock('landscape')`. MUST only be called from
 * a user gesture; MUST never throw (unsupported API, permission denial and
 * non-fullscreen refusals are all expected outcomes).
 */
function attemptLandscapeLock(): void {
  try {
    const lock = readLandscapeLock();
    if (!lock) return;
    const pending = lock('landscape');
    if (pending && typeof pending.catch === 'function') {
      pending.catch(() => { /* permission denied / unsupported — best effort */ });
    }
  } catch {
    /* never throw from a policy overlay */
  }
}

const noticeStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // Above all in-game chrome (max observed 99999: ErrorToast / DevStatusBar /
  // WindowLauncher; screensaver 9001) yet BELOW the fatal crash overlay
  // (999999) so a crash surface stays diagnosable when held in portrait.
  zIndex: 900000,
  background: 'linear-gradient(160deg, #060a14 0%, #101b2e 55%, #0a1220 100%)',
  color: '#e8f4ff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 18,
  padding: 24,
  textAlign: 'center',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  userSelect: 'none',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
};

const bodyStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  opacity: 0.85,
  maxWidth: 320,
  lineHeight: 1.5,
};

const buttonStyle: React.CSSProperties = {
  marginTop: 4,
  padding: '12px 22px',
  borderRadius: 10,
  border: '1px solid rgba(140, 200, 255, 0.5)',
  background: 'rgba(70, 140, 220, 0.25)',
  color: '#e8f4ff',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const RotateDeviceNotice: React.FC = () => {
  const { width, height } = useViewportSize();
  // Feature check runs once — availability cannot change while mounted.
  const [lockAvailable] = useState(canAttemptLandscapeLock);
  const [lockRequested, setLockRequested] = useState(false);

  const handleRotateNow = useCallback(() => {
    setLockRequested(true);
    attemptLandscapeLock();
  }, []);

  if (!shouldShowRotateNotice(width, height)) return null;

  return (
    <div
      data-testid="rotate-device-notice"
      role="alert"
      aria-label="Rotate your device to landscape"
      style={noticeStyle}
    >
      <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden="true" focusable="false">
        {/* Portrait phone outline */}
        <rect x="31" y="16" width="26" height="46" rx="7" fill="none" stroke="#7fb8ff" strokeWidth="3.5" />
        <line x1="38" y1="54" x2="50" y2="54" stroke="#7fb8ff" strokeWidth="3.5" strokeLinecap="round" />
        {/* Rotation arrows */}
        <path d="M15 55 A 30 30 0 0 1 22 25" fill="none" stroke="#d7ecff" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M15 55 l-7 -5 M15 55 l2 -8.5" fill="none" stroke="#d7ecff" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M73 33 A 30 30 0 0 1 66 63" fill="none" stroke="#d7ecff" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M73 33 l7 5 M73 33 l-2 8.5" fill="none" stroke="#d7ecff" strokeWidth="3.5" strokeLinecap="round" />
      </svg>
      <div style={titleStyle}>Rotate your device</div>
      <div style={bodyStyle}>
        Helix 2000 plays in landscape. Turn your phone sideways to continue.
      </div>
      {lockAvailable && (
        <button type="button" onClick={handleRotateNow} style={buttonStyle}>
          {lockRequested ? 'Rotation requested' : 'Rotate now'}
        </button>
      )}
    </div>
  );
};
